<script>
import PartnerLinkAdd from '@/components/page-partners/PartnerLinkAdd.vue';
import PartnerFunnel from '@/components/page-partners/PartnerFunnel.vue';
import Button from 'primevue/button';
import Tag from 'primevue/tag';

export default {
    name: 'PartnerLinkList',

    components: {
        PartnerLinkAdd,
        PartnerFunnel,
        Button,
        Tag,
    },

    props: {
        partnerId: {
            type: Number,
            required: true,
        },
    },

    data() {
        return {
            copiedId: null,
            expandedFunnelLinkId: null,
            showDeleteLinkModal: false,
            pendingDeleteLinkId: null,
            isDeletingLink: false,
        };
    },

    computed: {
        links() {
            return this.$store.getters.PARTNER_LINKS.filter(
                (l) => l.partner_id === this.partnerId
            );
        },
        isLoading() {
            return this.$store.getters.IS_LINKS_LOADING;
        },
    },

    methods: {
        shortSlug(slug) {
            if (!slug) return '';
            return slug.length > 8 ? slug.slice(0, 8) + '…' : slug;
        },

        testLandingHref(slug) {
            // Compute the test-landing URL using the current host (always correct for local/dev/prod)
            const href = `${window.location.origin}/landing?ref=${slug}`;
            console.debug('[FIX] testLandingHref computed:', href);
            return href;
        },

        async copyUrl(link) {
            try {
                await navigator.clipboard.writeText(link.full_url);
                this.copiedId = link.id;
                setTimeout(() => {
                    this.copiedId = null;
                }, 1500);
            } catch {
                // fallback: noop
            }
        },

        async confirmDeleteLink(linkId) {
            this.pendingDeleteLinkId = linkId;
            this.showDeleteLinkModal = true;
        },

        async executeDeleteLink() {
            this.isDeletingLink = true;
            console.debug('[PartnerLinkList] deleting link', this.pendingDeleteLinkId);
            try {
                await this.$store.dispatch('removePartnerLink', {
                    partnerId: this.partnerId,
                    linkId: this.pendingDeleteLinkId,
                });
                console.debug('[PartnerLinkList] link deleted', this.pendingDeleteLinkId);
            } catch (e) {
                console.error('[PartnerLinkList] delete failed', this.pendingDeleteLinkId, e);
            } finally {
                this.isDeletingLink = false;
                this.showDeleteLinkModal = false;
                this.pendingDeleteLinkId = null;
            }
        },

        cancelDeleteLink() {
            this.showDeleteLinkModal = false;
            this.pendingDeleteLinkId = null;
        },

        toggleFunnel(linkId) {
            this.expandedFunnelLinkId = this.expandedFunnelLinkId === linkId ? null : linkId;
        },
    },
};
</script>

<template>
    <div class="partner-links-panel">
        <!-- Add link form -->
        <PartnerLinkAdd :partner-id="partnerId" />

        <div v-if="isLoading" class="links-state">Loading links…</div>

        <div v-else-if="links.length === 0" class="links-state links-empty">
            No tracking links yet. Use the form above to create the first one.
        </div>

        <template v-else>
            <div class="links-table-header">
                <span>Slug</span>
                <span>URL</span>
                <span>Description</span>
                <span>Created</span>
                <span></span>
            </div>
            <template v-for="link in links" :key="link.id">
                <div class="links-row">
                    <Tag
                        :value="shortSlug(link.slug)"
                        severity="secondary"
                        class="slug-tag"
                        :title="link.slug"
                    />
                    <span class="url-cell">
                        <a
                            :href="link.full_url"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="url-mono url-link"
                            :title="link.full_url"
                        >{{ link.full_url }}</a>
                        <a
                            :href="testLandingHref(link.slug)"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="test-landing-link"
                            title="Open test landing page (current host)"
                        >↗ Test</a>
                        <Button
                            :label="copiedId === link.id ? '✓ Copied' : 'Copy'"
                            :severity="copiedId === link.id ? 'success' : 'info'"
                            size="small"
                            outlined
                            @click="copyUrl(link)"
                        />
                    </span>
                    <span class="desc-cell">{{ link.description ?? '—' }}</span>
                    <span class="date-cell">{{ new Date(link.created_at).toLocaleDateString() }}</span>
                    <span class="actions-cell">
                        <Button
                            label="Funnel"
                            size="small"
                            :severity="expandedFunnelLinkId === link.id ? 'primary' : 'secondary'"
                            :outlined="expandedFunnelLinkId !== link.id"
                            @click="toggleFunnel(link.id)"
                        />
                        <Button
                            label="Delete"
                            size="small"
                            severity="danger"
                            outlined
                            @click="confirmDeleteLink(link.id)"
                        />
                    </span>
                </div>
                <div v-if="expandedFunnelLinkId === link.id" :key="`funnel-${link.id}`" class="funnel-row">
                    <PartnerFunnel :partner-id="partnerId" :link-id="link.id" />
                </div>
            </template>
        </template>
    </div>

    <!-- Delete link confirmation modal -->
    <Teleport to="body">
        <div v-if="showDeleteLinkModal" class="del-overlay" @click.self="cancelDeleteLink">
            <div class="del-modal" role="dialog" aria-modal="true">
                <div class="del-modal__icon">🔗</div>
                <div class="del-modal__title">Delete tracking link?</div>
                <div class="del-modal__body">
                    This link and all its analytics data will be permanently deleted.
                    This action cannot be undone.
                </div>
                <div class="del-modal__actions">
                    <Button
                        label="Cancel"
                        severity="secondary"
                        :disabled="isDeletingLink"
                        @click="cancelDeleteLink"
                    />
                    <Button
                        label="Delete"
                        severity="danger"
                        :loading="isDeletingLink"
                        @click="executeDeleteLink"
                    />
                </div>
            </div>
        </div>
    </Teleport>
</template>

<style scoped>
.partner-links-panel {
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 12px;
}
.links-state {
    font-size: 0.85rem;
    color: #64748b;
    padding: 8px 0;
}
.links-empty {
    text-align: center;
    padding: 20px 0;
    color: #94a3b8;
    font-size: 0.82rem;
}
.links-table-header {
    display: grid;
    grid-template-columns: 120px 1fr 160px 90px 160px;
    gap: 8px;
    padding: 4px 8px;
    font-size: 0.72rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #94a3b8;
    border-bottom: 1px solid #e5e7eb;
}
.links-row {
    display: grid;
    grid-template-columns: 120px 1fr 160px 90px 160px;
    gap: 8px;
    align-items: center;
    padding: 8px;
    border-bottom: 1px solid #f1f5f9;
    font-size: 0.83rem;
}
.links-row:hover {
    background: #f8fafc;
    border-radius: 4px;
}
.slug-badge {
    font-family: monospace;
    font-size: 0.78rem;
    background: #f1f5f9;
    border: 1px solid #e2e8f0;
    border-radius: 4px;
    padding: 2px 7px;
    color: #334155;
    display: inline-block;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.url-cell {
    display: flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
}
.url-mono {
    font-family: monospace;
    font-size: 0.78rem;
    color: #475569;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex: 1;
    min-width: 0;
}
.url-link {
    text-decoration: none;
    color: #10b981;
}
.url-link:hover {
    text-decoration: underline;
    color: #059669;
}
.test-landing-link {
    font-size: 0.72rem;
    font-weight: 600;
    color: #f59e0b;
    text-decoration: none;
    white-space: nowrap;
    flex-shrink: 0;
}
.test-landing-link:hover {
    text-decoration: underline;
    color: #d97706;
}
.btn-copy {
    font-size: 0.72rem;
    padding: 2px 9px;
    cursor: pointer;
    background: #e0f2fe;
    border: 1px solid #bae6fd;
    border-radius: 4px;
    color: #0369a1;
    white-space: nowrap;
    flex-shrink: 0;
    transition: background 0.15s;
}
.btn-copy.copied {
    background: #bbf7d0;
    border-color: #86efac;
    color: #065f46;
}
.desc-cell {
    font-size: 0.82rem;
    color: #64748b;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.date-cell {
    font-size: 0.78rem;
    color: #94a3b8;
}
.actions-cell {
    display: flex;
    gap: 6px;
}
.btn-action {
    font-size: 0.75rem;
    padding: 3px 10px;
    border-radius: 4px;
    cursor: pointer;
    background: transparent;
    border: 1px solid #cbd5e1;
    color: #475569;
    transition: all 0.15s;
}
.btn-action:hover {
    background: #f1f5f9;
}
.btn-action--active {
    background: #dbeafe;
    border-color: #93c5fd;
    color: #1d4ed8;
}
.btn-action--danger {
    border-color: #fca5a5;
    color: #dc2626;
}
.btn-action--danger:hover {
    background: #fef2f2;
}
.funnel-row {
    background: #f0f9ff;
    border-radius: 4px;
    margin-bottom: 4px;
}
</style>
